import { useDepot } from '#~/depot/hooks.js';
import { markAllRead, useMultiAddBucketNotifications } from '#~/notifications/index.js';
import { unreadActionableCount } from '#~/notifications/unread-actionable-count.js';
import { Sel, STagNew } from '#~/stag/index.js';
import { usePopupStore } from '#~/store/popup.js';
import { NotificationTray } from './tray.jsx';
import type { Props } from './types.js';
import { AnimatePresence, motion, Motions } from '@studio/design-system/motion';
import { Indicator } from '@studio/design-system/notification';
import { toObjectValues } from '@tryiris-ai/js-types';
import { useEffect, useRef } from 'react';
import type { ReactElement } from 'react';
import { twMerge } from 'cn';
import { useAnalytics, EVENT_CATEGORIES, EVENT_NAMES } from '#~/analytics/index.js';

// Keep camera subscriptions in their own leaf so unrelated room ticks cannot rerender the bell.
const MultiAddNotificationObserver = (): null => {
  useMultiAddBucketNotifications();
  return null;
};

/**
 * `<Notifications>` component — masthead bell + tray.
 *
 * Bug 3 (spec §3.3): the bell badge is now the primitive unread actionable count of
 * the general notification queue — NOT the count of the removed
 * `multiAddBatchNotifications` slice (that slice never self-resolved for
 * PTZ-less cameras, so the badge stuck forever). Opening + closing the
 * tray runs `markAllRead`, which drains the unread count and clears the
 * badge for every camera shape, including the no-PTZ case.
 *
 * Per `ui-architecture` (ai.knowledge/global): badge color uses the
 * `bg-error` Tailwind theme token — the Hive variant resolves the same
 * token to its own brand color via the design-system theme config.
 * No literal `bg-red-500` etc.
 *
 * Per `reference-rules-in-comments` (ai.knowledge/global).
 */
export const Notifications = ({ className, ...props }: Props): ReactElement => {
  // Select the notifications map by identity. Mapping inside the selector
  // allocates a new array on every depot tick, so agents/rooms updates
  // would rerender the Indicator (Object.is). Per rule: stay-in-scope
  // (ai.knowledge/global) — keep the #1776 selectors, no useMemo rewrite.
  const notifications = useDepot((depot) => depot.notifications);
  const allNotifications = toObjectValues(notifications)
    .sort((a, b) => (a[0].timestamp < b[0].timestamp ? 1 : -1))
    .map(([notification, , { isRead }]) => ({
      ...notification,
      isRead
    }));

  const unreadCount = useDepot((state) => unreadActionableCount(state.notifications));
  const { track } = useAnalytics();
  const isActive = unreadCount > 0;

  const { popup, setPopup } = usePopupStore();
  const show = popup == 'notifications';

  // Wraps the bell; used to return focus to it when the tray closes.
  const bellWrapRef = useRef<HTMLDivElement>(null);
  const wasShown = useRef(false);

  const onClose = (): void => {
    markAllRead();
    setPopup(false);
  };

  useEffect(() => {
    const fn = (): void => {
      if (show) {
        onClose();
      }
    };

    document.addEventListener('click', fn);

    return () => {
      document.removeEventListener('click', fn);
    };
  }, [show]);

  useEffect(() => {
    // Per rule: keyboard-reachable-interactive-elements (ai.knowledge/global) —
    // when the tray closes, return focus to the bell so a keyboard user is not
    // left at the top of the document (spec §07). The bell is the first button
    // in the wrapper; the tray has already unmounted by the time this runs.
    if (wasShown.current && !show) {
      bellWrapRef.current?.querySelector('button')?.focus();
    }

    wasShown.current = show;
  }, [show]);

  const onToggle = (event: React.MouseEvent<HTMLButtonElement>): void => {
    event.stopPropagation();

    if (show) {
      onClose();
    } else {
      setPopup('notifications');
    }
  };

  // Announce only the actionable unread count with correct plurals; zero (and
  // informational-only unread, which is not counted) stays the plain label. Spec §04.
  const ariaLabel =
    unreadCount > 0
      ? `${unreadCount} unread ${unreadCount === 1 ? 'alert that needs attention' : 'alerts that need attention'}`
      : 'Notifications';

  return (
    <div className='relative' ref={bellWrapRef}>
      <MultiAddNotificationObserver />
      <Indicator
        {...STagNew(Sel.Notifications, 'toggle')}
        aria-label={ariaLabel}
        className={twMerge('text-center', className)}
        isActive={isActive}
        onClick={onToggle}
        {...props}
      />
      {unreadCount > 0 && (
        // Badge test hook uses an STag, not a raw data-testid
        // (rule: playwright-stag-selectors-only, ai.knowledge/global).
        <span
          aria-hidden='true'
          className={twMerge(
            'absolute -right-2 -top-1 min-w-[18px] rounded-full px-4 py-0',
            'bg-error text-xs font-semibold leading-[18px] text-white',
            'pointer-events-none text-center'
          )}
          {...STagNew(Sel.Notifications, 'bell_badge')}
        >
          {unreadCount}
        </span>
      )}

      <AnimatePresence>
        {!!show && (
          <motion.div
            {...Motions.Fade}
            className='absolute right-[-40px] top-[calc(100%_+_16px)] z-50'
            onClick={(event): void => {
              event.stopPropagation();
              track(EVENT_NAMES.CLICK_NOTIFICATIONS_TRAY, EVENT_CATEGORIES.NOTIFICATIONS);
            }}
          >
            <div className='absolute right-[46px] top-[-20px] h-0 w-0 border-[10px] border-transparent border-b-light' />
            <NotificationTray notifications={allNotifications} />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
